import {
  Binding,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  Type,
  ViewContainerRef,
} from '@angular/core';

import { Observable, take } from 'rxjs';

import { ModalButton, NotificationModalComponent } from './notification/modal/notification-modal.component';
import { NotificationToastComponent } from './notification/notification-toast.component';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly injector = inject(EnvironmentInjector);

  private vcr?: ViewContainerRef;

  init(vcr: ViewContainerRef) {
    if (this.vcr) {
      throw new Error('ToastService already initialized');
    }
    this.vcr = vcr;
  }

  showToast(config: NotificationConfig): void {
    this.createNotification(config);
  }

  showModal(config: ModalConfig): void {
    this.createModal(config);
  }

  private createNotification(config: NotificationConfig): void {
    const { type = 'default', title = '', message, duration = 5 } = config;

    if (!this.vcr) {
      throw new Error('ToastService not initialized');
    }

    // Create component
    const componentRef = createComponent(NotificationToastComponent, { environmentInjector: this.injector });

    // Set input properties
    componentRef.setInput('type', type);
    componentRef.setInput('titleString', title);
    componentRef.setInput('message', message);
    componentRef.setInput('closeAfter', duration);

    componentRef.changeDetectorRef.detectChanges();

    this.vcr.insert(componentRef.hostView);

    // Clean up
    componentRef.instance.closeNotification.pipe(take(1)).subscribe(() => this.removeNotification(componentRef));
  }

  private removeNotification(componentRef: ComponentRef<NotificationToastComponent>): void {
    this.vcr?.remove(this.vcr?.indexOf(componentRef.hostView));
  }

  private createModal(config: ModalConfig): void {
    if (!this.vcr) {
      throw new Error('ToastService not initialized');
    }

    // Create component
    const componentRef = createComponent(config.comp ?? NotificationModalComponent, {
      environmentInjector: this.injector,
      bindings: config.bindings,
    });

    // Set input properties
    Object.entries(config.data || {}).forEach(([key, value]) => {
      componentRef.setInput(key, value);
    });

    componentRef.changeDetectorRef.detectChanges();

    this.vcr.insert(componentRef.hostView);

    // Clean up
    componentRef.instance.close.pipe(take(1)).subscribe(() => this.removeModal(componentRef));
  }

  private removeModal(componentRef: ComponentRef<ModalComponent>): void {
    this.vcr?.remove(this.vcr?.indexOf(componentRef.hostView));
  }
}

export type NotificationType = 'success' | 'error' | 'default';

export interface NotificationConfig {
  type?: NotificationType;
  title?: string;
  message: string;
  duration?: number;
}

export interface ModalConfig {
  comp?: Type<ModalComponent>;
  data?: ModalData;
  bindings?: Binding[];
}

export interface ModalData {
  titleString?: string;
  messages?: string | string[];
  buttons?: ModalButton[];
  [key: string]: unknown;
}

export interface ModalComponent {
  close: Observable<void>;
}
